import React, { FocusEvent, PointerEvent, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from "react-dom";
import styled from "styled-components";
import Colours from '@shared/ui/atoms/Colours';
import { BodySmall, BodyVerySmall } from '@shared/ui/atoms/fonts/Body';
import { binStyle, HeatBin } from "./heatScale";

export interface HeatTooltipContent {
  title: string;
  // Under the title, such as which day a grid square is for
  subtitle?: string;
  lines: string[];
  // Shown beside the first line
  bin: HeatBin;
}

interface Tip extends HeatTooltipContent {
  x: number;
  top: number;
  bottom: number;
}

// Space kept between the tooltip and the mark, and the edge of the window
const GAP = 8;

// Tooltip for the grid squares, desk tiles and floor plan marks. Shows on hover,
// focus or tap, and hides on scroll so it never points at the wrong mark.
export function useHeatTooltip() {
  const [tip, setTip] = useState<Tip | undefined>();
  const hide = useCallback(() => setTip(undefined), []);

  useEffect(() => {
    if (!tip) return;
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    return () => {
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
    };
  }, [tip, hide]);

  const markProps = useCallback((ariaLabel: string, content: HeatTooltipContent) => {
    const show = (target: HTMLElement) => {
      const rect = target.getBoundingClientRect();
      setTip({...content, x: rect.left + rect.width / 2, top: rect.top, bottom: rect.bottom});
    };
    return {
      tabIndex: 0,
      'aria-label': ariaLabel,
      onPointerEnter: (e: PointerEvent<HTMLElement>) => show(e.currentTarget),
      // Lifting a finger after a tap also fires pointerleave, so only a mouse leaving hides it
      onPointerLeave: (e: PointerEvent<HTMLElement>) => { if (e.pointerType === 'mouse') hide(); },
      onFocus: (e: FocusEvent<HTMLElement>) => show(e.currentTarget),
      onBlur: hide,
    };
  }, [hide]);

  return {tip, markProps, hide};
}

export function HeatTooltip({tip}: { tip?: Tip }) {
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ left: number; top: number } | undefined>();

  // Measured before paint: centred above the mark, or below it when there is no room
  useLayoutEffect(() => {
    if (!tip || !ref.current) {
      setPosition(undefined);
      return;
    }
    const {width, height} = ref.current.getBoundingClientRect();
    const left = Math.max(GAP, Math.min(tip.x - width / 2, window.innerWidth - width - GAP));
    const top = tip.top - height - GAP >= GAP ? tip.top - height - GAP : tip.bottom + GAP;
    setPosition({left, top});
  }, [tip]);

  if (!tip) return null;

  return createPortal(
    <Container ref={ref} role="tooltip" style={position ?? {left: 0, top: 0, visibility: 'hidden'}}>
      <Title weight={600}>{tip.title}</Title>
      {tip.subtitle && <BodyVerySmall colour={Colours.darkGrey} style={{margin: '0 0 4px 0'}}>{tip.subtitle}</BodyVerySmall>}
      {tip.lines.map((line, index) => (
        <Line key={index}>
          {index === 0 && <Swatch style={binStyle(tip.bin)} />}
          <BodyVerySmall>{line}</BodyVerySmall>
        </Line>
      ))}
    </Container>,
    document.body
  );
}

// A compact version of the recharts CustomToolTip used on the other reports
const Container = styled.div`
  position: fixed;
  z-index: 1000;
  max-width: 280px;
  background-color: white;
  padding: 12px 16px;
  box-shadow: 0 2px 8px 2px rgba(76, 109, 172, 0.15);
  border-radius: 12px;
  pointer-events: none;
`

const Title = styled(BodySmall)`
  border-bottom: 1px solid ${Colours.mildGrey};
  padding-bottom: 6px;
  margin: 0 0 6px 0;
`

const Line = styled.div`
  display: flex;
  align-items: center;
  p {
    margin: 0;
  }
`

const Swatch = styled.span`
  flex-shrink: 0;
  width: 10px;
  height: 10px;
  margin-right: 6px;
  border-radius: 100%;
  box-shadow: inset 0 0 0 1px rgba(8, 30, 34, 0.15);
`
